// Desktop-style taskbar — replaces the old TopNav on md+ screens. Ported from
// the /dev desktop preview: dark translucent bar, app-tray launcher, running-
// window buttons, and (instead of the preview's wifi/volume placeholders) the
// real right-side tools: search, notifications, my tasks, dev, settings, user.
// All menus are CLICK-toggled (not hover) so they work on touchscreens.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useWindowManager } from '@/lib/context/WindowManagerContext';
import { APP_MENU_GROUPS, type AppMenuItem } from './appMenu';
import { NavSubTree, TREE_ROOT_LOADERS } from '@/components/layout/NavTreeItem';
import { usePins, togglePin } from '@/components/layout/PinnedPages';
import { useDesktopMode } from '@/lib/hooks/useDesktopMode';
import { ControlCenter } from './ControlCenter';

const isActive = (pathname: string, href: string) =>
  pathname === href || pathname.startsWith(href + '/');

/** Click-outside-to-close wrapper for taskbar dropdowns. */
function Dropdown({
  open,
  onClose,
  trigger,
  children,
  align = 'left',
}: {
  open: boolean;
  onClose: () => void;
  trigger: ReactNode;
  children: ReactNode;
  align?: 'left' | 'right';
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [open, onClose]);

  return (
    <div className="relative" ref={ref}>
      {trigger}
      {open && (
        <div
          className={`absolute top-full mt-1.5 ${align === 'left' ? 'left-0' : 'right-0'} bg-[#1e1e2e]/95 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl z-[70] overflow-hidden`}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// ── App tray: touch-friendly main-menu list tree ──────────────────────────────

function AppTray() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(APP_MENU_GROUPS.map(g => g.label)) // all groups open by default
  );
  const [subOpen, setSubOpen] = useState<Set<string>>(new Set());
  const { pathname } = useLocation();
  const { openWindow } = useWindowManager();
  const pins = usePins();

  // Pop any tree node (folder, child folder, or record) out into a window.
  const popOutNode = (href: string, label: string) => {
    setOpen(false);
    openWindow({ route: href, title: label, icon: 'folder_open' });
  };

  const toggleGroup = (label: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });

  // #190: folder-style menu items (they have a browse tree) expand the tree in
  // place instead of opening a window — double-click still opens the page.
  // Everything else keeps #182: the click IS the pop-out.
  const go = (item: AppMenuItem) => {
    if (!item.external && item.href in TREE_ROOT_LOADERS) {
      toggleSub(item.href);
      return; // tray stays open — the point is to browse
    }
    setOpen(false);
    if (item.external) window.open(item.href, '_blank', 'noopener');
    else openWindow({ route: item.href, title: item.label, icon: item.icon });
  };

  const goWindow = (item: AppMenuItem) => {
    if (item.external) return;
    setOpen(false);
    openWindow({ route: item.href, title: item.label, icon: item.icon });
  };

  const toggleSub = (href: string) =>
    setSubOpen(prev => {
      const next = new Set(prev);
      if (next.has(href)) next.delete(href);
      else next.add(href);
      return next;
    });

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      trigger={
        <button
          onClick={() => setOpen(v => !v)}
          className={`flex items-center gap-2 h-9 pl-2 pr-3 rounded-lg transition-colors ${
            open ? 'bg-[#C77D3F]/30' : 'hover:bg-white/10'
          }`}
          title="Menu"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#C77D3F">
            <path d="M12 2L2 20h20L12 2zm0 3.5L18.5 18h-13L12 5.5z" />
          </svg>
          <span className="text-sm font-bold text-gray-100 hidden lg:block">OchrePoint</span>
        </button>
      }
    >
      <div className="w-[300px] max-h-[calc(100vh-6rem)] overflow-y-auto py-2 overscroll-contain">
        {/* Pinned — same store as the breadcrumb pin bar and mobile drawer */}
        {pins.length > 0 && (
          <div className="px-2 pb-1">
            <p className="px-3 pt-1 pb-0.5 text-xs font-bold uppercase tracking-widest text-gray-500">Pinned</p>
            {pins.map(p => (
              <div key={p.path} className="flex items-center rounded-xl ml-3 hover:bg-white/10">
                <button
                  onClick={() => {
                    setOpen(false);
                    openWindow({ route: p.path, title: p.label, icon: 'push_pin' });
                  }}
                  className="flex-1 flex items-center gap-3 min-h-[44px] px-3 text-left min-w-0"
                >
                  <span className="material-symbols-outlined text-lg text-[#C77D3F]" style={{ fontVariationSettings: "'FILL' 1" }}>push_pin</span>
                  <span className="text-sm font-medium text-gray-200 truncate">{p.label}</span>
                </button>
                <button
                  onClick={() => togglePin(p)}
                  className="w-11 h-11 flex items-center justify-center rounded-xl text-gray-500 hover:text-red-400 hover:bg-white/10 flex-shrink-0"
                  title="Unpin"
                >
                  <span className="material-symbols-outlined text-lg">close</span>
                </button>
              </div>
            ))}
          </div>
        )}
        {APP_MENU_GROUPS.map(group => (
          <div key={group.label} className="px-2 pb-1">
            {/* Group header — big touch target */}
            <button
              onClick={() => toggleGroup(group.label)}
              className="w-full flex items-center gap-2.5 min-h-[44px] px-3 rounded-xl text-left hover:bg-white/5 transition-colors"
            >
              <span className="material-symbols-outlined text-lg text-[#C77D3F]">
                {group.icon}
              </span>
              <span className="flex-1 text-xs font-bold uppercase tracking-widest text-gray-400">
                {group.label}
              </span>
              <span
                className={`material-symbols-outlined text-lg text-gray-500 transition-transform ${
                  expanded.has(group.label) ? 'rotate-180' : ''
                }`}
              >
                expand_more
              </span>
            </button>

            {/* Items */}
            {expanded.has(group.label) &&
              group.items.map(item => (
                <div key={item.href}>
                  <div
                    className={`flex items-center rounded-xl ml-3 ${
                      !item.external && isActive(pathname, item.href)
                        ? 'bg-[#C77D3F]/20'
                        : 'hover:bg-white/10'
                    }`}
                  >
                    <button
                      onClick={() => go(item)}
                      onDoubleClick={() => goWindow(item)}
                      title={item.href in TREE_ROOT_LOADERS ? `Click to browse · double-click to open ${item.label}` : undefined}
                      className="flex-1 flex items-center gap-3 min-h-[44px] px-3 text-left"
                    >
                      <span
                        className={`material-symbols-outlined text-lg ${
                          !item.external && isActive(pathname, item.href)
                            ? 'text-[#C77D3F]'
                            : 'text-gray-400'
                        }`}
                      >
                        {item.icon}
                      </span>
                      <span className="text-sm font-medium text-gray-200">{item.label}</span>
                      {item.external && (
                        <span className="material-symbols-outlined text-sm text-gray-500">
                          open_in_new
                        </span>
                      )}
                    </button>
                    {/* Pin to the shared pin list (menu + breadcrumb chip bar) */}
                    {!item.external && (
                      <button
                        onClick={() => togglePin({ path: item.href, label: item.label })}
                        className={`w-9 h-11 flex items-center justify-center rounded-xl hover:bg-white/10 flex-shrink-0 ${
                          pins.some(p => p.path === item.href) ? 'text-[#C77D3F]' : 'text-gray-500 hover:text-[#C77D3F]'
                        }`}
                        title={pins.some(p => p.path === item.href) ? 'Unpin' : `Pin ${item.label}`}
                      >
                        <span className="material-symbols-outlined text-base"
                          style={pins.some(p => p.path === item.href) ? { fontVariationSettings: "'FILL' 1" } : undefined}>
                          push_pin
                        </span>
                      </button>
                    )}
                    {/* Expand the recursive folder-navigator tree */}
                    {item.href in TREE_ROOT_LOADERS && (
                      <button
                        onClick={() => toggleSub(item.href)}
                        className="w-9 h-11 flex items-center justify-center rounded-xl text-gray-500 hover:text-[#C77D3F] hover:bg-white/10 flex-shrink-0"
                        title={`Browse ${item.label}`}
                      >
                        <span className={`material-symbols-outlined text-lg transition-transform ${subOpen.has(item.href) ? 'rotate-180' : ''}`}>
                          expand_more
                        </span>
                      </button>
                    )}
                  </div>
                  {/* #113 — recursive breadcrumb-order tree (Clients › client ›
                      Jobs › job › sections › records/files); every node can pin
                      and pop out into a floating window */}
                  {item.href in TREE_ROOT_LOADERS && subOpen.has(item.href) && (
                    <NavSubTree rootHref={item.href} dark searchable onNavigate={() => setOpen(false)} onPopOut={popOutNode} />
                  )}
                </div>
              ))}
          </div>
        ))}
      </div>
    </Dropdown>
  );
}

// ── Current page as a taskbar item (#188) ─────────────────────────────────────
// The base page shows in the bar exactly like a popped-out window: clicking it
// turns it INTO a window (returning the base view home), the X closes it (goes
// home). Complements #185, where maximizing a window turns it into the page.

function CurrentPageButton() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { openWindow } = useWindowManager();
  const desktopMode = useDesktopMode();

  const home = desktopMode ? '/desktop' : '/dashboard';
  if (pathname === home || pathname === '/desktop') return null;

  // Best label/icon for the current route from the app menu; fall back gracefully.
  let title = 'Page';
  let icon = 'web_asset';
  let best = '';
  for (const g of APP_MENU_GROUPS) {
    for (const it of g.items) {
      if (it.external) continue;
      if ((pathname === it.href || pathname.startsWith(it.href + '/')) && it.href.length > best.length) {
        best = it.href; title = it.label; icon = it.icon;
      }
    }
  }

  // The maximized page becomes a floating window again (#198) — same action as
  // clicking the label, but as an explicit, discoverable affordance.
  const unmaximize = () => { openWindow({ route: pathname, title, icon }); navigate(home); };

  return (
    <div className="flex items-center h-9 rounded-lg text-xs font-medium max-w-[210px] flex-shrink-0 bg-[#C77D3F]/30 text-[#C77D3F] border border-[#C77D3F]/30">
      <button
        onClick={unmaximize}
        className="flex items-center gap-1.5 h-full pl-2.5 min-w-0"
        title={`${title} — click to turn into a window`}
      >
        <span className="material-symbols-outlined text-sm">{icon}</span>
        <span className="truncate">{title}</span>
      </button>
      <button
        onClick={unmaximize}
        className="flex items-center justify-center w-6 h-full text-[#C77D3F]/70 hover:text-white hover:bg-[#C77D3F]/60 flex-shrink-0"
        title="Unmaximize — back to a window"
      >
        <span className="material-symbols-outlined text-sm">close_fullscreen</span>
      </button>
      <button
        onClick={() => navigate(home)}
        className="flex items-center justify-center w-6 h-full rounded-r-lg text-[#C77D3F]/70 hover:text-white hover:bg-red-500/70 flex-shrink-0"
        title={`Close ${title}`}
      >
        <span className="material-symbols-outlined text-sm">close</span>
      </button>
    </div>
  );
}

// ── Running-window taskbar buttons ────────────────────────────────────────────

function WindowButtons() {
  const { windows, activeId, minimizeWindow, restoreWindow, focusWindow, closeWindow, tileWindows } = useWindowManager();
  if (windows.length === 0) return null;
  return (
    <div className="flex items-center gap-1 min-w-0 overflow-x-auto scrollbar-none">
      {windows.map(win => (
        <div
          key={win.id}
          className={`flex items-center h-9 rounded-lg text-xs font-medium max-w-[180px] flex-shrink-0 transition-colors ${
            activeId === win.id && !win.minimized
              ? 'bg-[#C77D3F]/30 text-[#C77D3F] border border-[#C77D3F]/30'
              : win.minimized
                ? 'bg-white/5 text-gray-500 hover:bg-white/10'
                : 'bg-white/10 text-gray-300 hover:bg-white/15'
          }`}
        >
          <button
            onClick={() =>
              win.minimized
                ? restoreWindow(win.id)
                : activeId === win.id
                  ? minimizeWindow(win.id)
                  : focusWindow(win.id)
            }
            className="flex items-center gap-1.5 h-full pl-2.5 min-w-0"
            title={win.title}
          >
            {win.pinned && <span className="material-symbols-outlined text-sm text-[#C77D3F]" style={{ fontVariationSettings: "'FILL' 1" }}>keep</span>}
            <span className="material-symbols-outlined text-sm">{win.icon}</span>
            <span className="truncate">{win.title}</span>
          </button>
          {/* #187: close straight from the taskbar */}
          <button
            onClick={() => closeWindow(win.id)}
            className="flex items-center justify-center w-6 h-full rounded-r-lg text-gray-500 hover:text-white hover:bg-red-500/70 flex-shrink-0"
            title={`Close ${win.title}`}
          >
            <span className="material-symbols-outlined text-sm">close</span>
          </button>
        </div>
      ))}
      {/* #187: tiling manager — arrange every open window in an even grid */}
      {windows.filter(w => !w.minimized).length > 1 && (
        <button
          onClick={tileWindows}
          className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-400 hover:text-[#C77D3F] hover:bg-white/10 flex-shrink-0"
          title="Tile windows"
        >
          <span className="material-symbols-outlined text-lg">grid_view</span>
        </button>
      )}
    </div>
  );
}
// ── Taskbar ───────────────────────────────────────────────────────────────────

export const Taskbar = ({ onSearchOpen }: { onSearchOpen?: () => void }) => {
  const { pathname } = useLocation();
  const desktopMode = useDesktopMode();

  return (
    <header className="hidden md:flex items-center h-12 px-2 gap-1 flex-shrink-0 z-50 bg-[#1e1e2e]/95 backdrop-blur-2xl border-b border-white/10">
      {/* App tray — main menu list tree */}
      <AppTray />

      {/* Back to the wallpaper desktop (#133) — only exists during the trial */}
      {desktopMode && (
        <Link
          to="/desktop"
          className={`flex items-center gap-1.5 h-9 px-2.5 rounded-lg text-xs font-medium flex-shrink-0 transition-colors ${
            isActive(pathname, '/desktop')
              ? 'text-[#C77D3F] bg-[#C77D3F]/20'
              : 'text-gray-300 hover:bg-white/10'
          }`}
          title="Show desktop"
        >
          <span className="material-symbols-outlined text-base">desktop_windows</span>
          <span className="hidden lg:inline">Desktop</span>
        </Link>
      )}

      <div className="w-px h-5 bg-white/10 mx-1" />

      {/* Current page shows like a window item (#188) */}
      <CurrentPageButton />

      {/* Open windows */}
      <WindowButtons />

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right-side tools — just search + the clock control center (#181);
          notifications, tasks, dev, settings AND the user account (#187) all
          live inside the control center now */}
      <button
        onClick={onSearchOpen}
        className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-white/10 transition-colors"
        title="Search (⌘K)"
      >
        <span className="material-symbols-outlined text-xl">search</span>
      </button>

      <ControlCenter />
    </header>
  );
};
